import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySlideToggleChange as MatSlideToggleChange } from '@angular/material/legacy-slide-toggle';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// CONFIGS
import { ChannelHubSpotPopupComponent } from '@app/modules/channels/channel-hubspot-sync-popup/channel-hubspot-sync-popup.component';
import { SnackBarConfig } from 'v2/modules/shared/configs/snackbar.config';
import { IChannel } from 'v2/modules/shared/interfaces/mtchannel.interface';
import { trackById } from 'v2/modules/shared/util/track-by.util';

import { IPagination } from '../../../shared/interfaces/paginator.interface';
import { IColumnConfig } from '../../../shared/interfaces/table.interface';
import { ChannelTableConfig } from '../../configs/channel-table.config';

import { DialogConfig } from './../../../shared/configs/dialog.config';
import { PaginationConfig } from './../../../shared/configs/paginator.config';

// COMPONENTS
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';

// INTERFACES

// SERVICES
import { ChannelFacadeService } from './../../channel-facade.service';
import { ChannelConfig } from './../../configs/channel.config';

// UTILS

@Component({
  selector: 'lum-channel-table',
  templateUrl: './channel-table.component.html',
  styleUrls: ['./channel-table.component.scss'],
})
export class ChannelTableComponent implements OnInit, OnDestroy {
  @Input()
  columns :IColumnConfig[] = ChannelTableConfig.columns;

  sortState = { column: '', direction: 'asc' };
  paginationConfig = PaginationConfig;
  channelTypes = ChannelConfig.channelTypes;
  channelTypeLabels = ChannelConfig.channelTypeLabels;

  trackById = trackById;
  private onDestroy$ = new Subject();

  constructor(
    private channelFacadeService :ChannelFacadeService,
    private snackBar :MatSnackBar,
    private dialog :MatDialog,
    private modal :NgbModal,
  ) {}

  ngOnInit() :void {
    this.channelFacadeService
      .getChannels(this.currentQueryParams)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: (error) => {
          this.snackBar.open(error.message, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onToggleStatusChange(event :MatSlideToggleChange, id :number) :void {
    this.snackBar.open('Channel status updating...', 'Info');
    this.channelFacadeService
      .updateStatus({ id, params: { status: event.checked ? '1' : '0' } })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: () => {
          this.snackBar.dismiss();
          this.snackBar.open('Channel status successfully updated.', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (response) => {
          this.snackBar.dismiss();
          this.snackBar.open(
            response?.error || 'Failed to update channel status.',
            'Fail',
          );
        },
      });
  }

  onConfirmDelete(id :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Delete',
          message: 'Are you sure you want to delete this channel?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.deleteChannel(id);
        }
      });
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.channelFacadeService.setCurrentQueryParams({
      ...this.currentQueryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    });
  }

  onRenewQuickbooksToken(id :number) :void {
    this.channelFacadeService
      .renewQuickbooksToken(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response && response.url) {
            window.location.href = response.url;
          }
        },
        error: (response) => {
          this.snackBar.open(
            response?.error || 'Failed to renew Quickbooks token.',
            'Fail',
          );
        },
      });
  }

  onRenewHubSpotTokens(id :number) :void {
    this.channelFacadeService
      .renewHubSpotTokens(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          window.open(response.url, '_blank');
        },
        error: (response) => {
          this.snackBar.open(
            response?.error || 'Failed to renew HubSpot token.',
            'Fail',
          );
        },
      });
  }

  onSyncHubSpot(channel :IChannel) {
    // This is a v1 implementation
    const modalRef = this.modal.open(ChannelHubSpotPopupComponent, {});
    modalRef.componentInstance.channel = channel;
  }

  onAuthorizeEcartApi(id :number) {
    this.channelFacadeService
      .authorizeEcartApi(id)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          window.open(response.url, '_blank');
        },
        error: (response) => {
          this.snackBar.open(
            response?.error || 'Failed to Authorize EcartApi Store token.',
            'Fail',
          );
        },
      });
  }

  onSortColumn(column :string) :void {
    const queryParams = { ...this.currentQueryParams };

    // Check if the current sort column is the same as the clicked column
    if (this.sortState.column === column) {
      if (this.sortState.direction === 'desc') {
        this.sortState = { column: 'created_at', direction: 'desc' }; // Default to 'created_at' with 'desc'
      } else {
        this.sortState.direction =
          this.sortState.direction === 'asc' ? 'desc' : 'asc';
      }
    } else {
      this.sortState = { column, direction: 'asc' };
    }

    queryParams.sortField = this.sortState.column;
    queryParams.sortDirection = this.sortState.direction;

    this.channelFacadeService.setCurrentQueryParams(queryParams);
  }

  private deleteChannel(id :number) :void {
    this.channelFacadeService.delete(id).subscribe({
      next: () => {
        this.snackBar.open('Channel successfully deleted.', 'Success', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
      error: (response) => {
        this.snackBar.open(response?.error || 'Failed to delete channel.', 'Fail');
      },
    });
  }

  get displayColumns() :{
    [key :string] :{ visible :boolean; label :string };
  } {
    return this.columns.reduce(
      (acc, { id, visible, label }) => {
        acc[id] = { visible, label };

        return acc;
      },
      {} as { [key :string] :{ visible :boolean; label :string } },
    );
  }

  // DIRECT VALUE ACCESSORS
  get currentQueryParams() :{ [key :string] :string | string[] } {
    return this.channelFacadeService.currentQueryParams;
  }

  get pagination() :IPagination {
    return this.channelFacadeService.pagination;
  }

  // OBSERVABLES ACCESSORS
  get loading$() :Observable<boolean> {
    return this.channelFacadeService.loading$;
  }

  get channels$() :Observable<IChannel[]> {
    return this.channelFacadeService.channels$;
  }
}
